import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, ShoppingBag, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { createCheckout } from "@/lib/api";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

const emptyShip = { name: "", email: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "US" };

const CartDrawer = () => {
  const { cart, open, setOpen, updateQty, removeFromCart } = useCart();
  const [step, setStep] = useState("cart"); // "cart" | "shipping"
  const [checkingOut, setCheckingOut] = useState(false);
  const [ship, setShip] = useState(() => {
    try { return JSON.parse(localStorage.getItem("gfx_ship") || "null") || emptyShip; }
    catch { return emptyShip; }
  });

  const closeAll = () => { setOpen(false); setStep("cart"); };

  const startCheckout = () => {
    if (cart.items.length === 0) return;
    setStep("shipping");
  };

  const submitShipping = async (e) => {
    e.preventDefault();
    const missing = ["name","email","line1","city","state","postal_code","country"].filter(k => !ship[k]?.trim());
    if (missing.length) {
      toast.error("Please fill out all required fields");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(ship.email)) {
      toast.error("Please enter a valid email");
      return;
    }
    try { localStorage.setItem("gfx_ship", JSON.stringify(ship)); } catch {}

    setCheckingOut(true);
    try {
      const { url } = await createCheckout({
        cart_id: cart.cart_id,
        origin_url: window.location.origin,
        shipping: ship,
      });
      if (!url) throw new Error("No checkout URL returned");
      window.location.href = url;
    } catch (e) {
      setCheckingOut(false);
      toast.error("Checkout failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const set = (k) => (e) => setShip((s) => ({ ...s, [k]: e.target.value }));

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={closeAll}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[80]"
            data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 220 }}
            className="fixed top-0 right-0 bottom-0 w-full sm:w-[440px] bg-[#0a0a0a] border-l border-white/10 z-[81] flex flex-col"
            data-testid="cart-drawer"
          >
            <header className="flex items-center justify-between px-6 py-5 border-b border-white/10">
              <div className="flex items-center gap-3">
                {step === "shipping" ? (
                  <button onClick={() => setStep("cart")} className="p-1 hover:bg-white/5" data-testid="ship-back">
                    <ArrowLeft className="w-4 h-4 text-[#007AFF]" />
                  </button>
                ) : (
                  <ShoppingBag className="w-4 h-4 text-[#007AFF]" strokeWidth={1.8} />
                )}
                <h3 className="font-heading text-2xl tracking-wider">
                  {step === "cart" ? "YOUR CART" : "SHIPPING"}
                </h3>
                {step === "cart" && (
                  <span className="text-xs uppercase tracking-[0.2em] text-white/40 font-body">{cart.count} items</span>
                )}
              </div>
              <button onClick={closeAll} className="p-2 hover:bg-white/5" data-testid="cart-close" aria-label="Close cart">
                <X className="w-4 h-4" />
              </button>
            </header>

            {step === "cart" && (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
                  {cart.items.length === 0 && (
                    <div className="text-center py-20 text-white/45 text-sm font-body" data-testid="cart-empty">
                      <ShoppingBag className="w-8 h-8 mx-auto mb-4 opacity-40" strokeWidth={1.4} />
                      Your cart is empty.
                    </div>
                  )}
                  {cart.items.map((it) => (
                    <div key={it.id} className="border border-white/10 p-4 flex gap-4" data-testid={`cart-item-${it.id}`}>
                      <div className="w-16 h-16 bg-[#121212] border border-white/10 flex items-center justify-center text-[#007AFF] font-heading text-lg">GFX</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-heading text-lg tracking-wide">GRIPFORCE X</div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-white/45 font-body truncate">Adjustable · 40–99 lb</div>
                          </div>
                          <button onClick={() => removeFromCart(it.id)} className="text-white/40 hover:text-red-400 transition" data-testid={`remove-${it.id}`} aria-label="Remove item">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="mt-3 flex items-center justify-between">
                          <div className="flex items-center border border-white/10">
                            <button onClick={() => updateQty(it.id, it.quantity - 1)} className="w-8 h-8 hover:bg-white/5" data-testid={`item-dec-${it.id}`} aria-label="Decrease quantity">
                              <Minus className="w-3 h-3 mx-auto" />
                            </button>
                            <span className="w-8 text-center text-sm font-body">{it.quantity}</span>
                            <button onClick={() => updateQty(it.id, it.quantity + 1)} className="w-8 h-8 hover:bg-white/5" data-testid={`item-inc-${it.id}`} aria-label="Increase quantity">
                              <Plus className="w-3 h-3 mx-auto" />
                            </button>
                          </div>
                          <div className="font-heading text-lg text-[#007AFF]">${(it.unit_price * it.quantity).toFixed(2)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <footer className="border-t border-white/10 px-6 py-5 space-y-3">
                  <div className="space-y-1.5">
                    <div className="flex items-baseline justify-between text-xs font-body">
                      <span className="uppercase tracking-[0.25em] text-white/50">Subtotal</span>
                      <span className="text-white/80" data-testid="cart-subtotal">${cart.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex items-baseline justify-between text-xs font-body">
                      <span className="uppercase tracking-[0.25em] text-white/50">Shipping</span>
                      <span className="text-white/80" data-testid="cart-shipping">${(cart.shipping ?? 0).toFixed(2)}</span>
                    </div>
                    <div className="flex items-baseline justify-between pt-2 border-t border-white/10">
                      <span className="text-[11px] uppercase tracking-[0.25em] text-white/60 font-body">Total</span>
                      <span className="font-heading text-3xl text-[#007AFF]" data-testid="cart-total">${(cart.total ?? cart.subtotal).toFixed(2)}</span>
                    </div>
                  </div>
                  <button
                    onClick={startCheckout}
                    disabled={cart.items.length === 0}
                    className="cta-glow w-full py-4 bg-[#007AFF] text-black font-body font-bold text-sm uppercase tracking-[0.25em] hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="cart-checkout"
                  >
                    Proceed to Checkout
                  </button>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/35 text-center font-body">Secure payment by Stripe</p>
                </footer>
              </>
            )}

            {step === "shipping" && (
              <form onSubmit={submitShipping} className="flex-1 flex flex-col overflow-hidden" data-testid="shipping-form">
                <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
                  <Field label="Full Name" value={ship.name} onChange={set("name")} testid="ship-name" autoComplete="name" required />
                  <Field label="Email" value={ship.email} onChange={set("email")} testid="ship-email" type="email" autoComplete="email" required />
                  <Field label="Street Address" value={ship.line1} onChange={set("line1")} testid="ship-line1" autoComplete="address-line1" required />
                  <Field label="Apt / Suite (Optional)" value={ship.line2} onChange={set("line2")} testid="ship-line2" autoComplete="address-line2" />
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="City" value={ship.city} onChange={set("city")} testid="ship-city" autoComplete="address-level2" required />
                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.25em] text-white/50 font-body mb-2">State</label>
                      <select
                        value={ship.state}
                        onChange={set("state")}
                        data-testid="ship-state"
                        autoComplete="address-level1"
                        required
                        className="w-full bg-[#121212] border border-white/15 px-3 py-3 text-sm font-body focus:border-[#007AFF] focus:outline-none"
                      >
                        <option value="">—</option>
                        {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="ZIP Code" value={ship.postal_code} onChange={set("postal_code")} testid="ship-postal" autoComplete="postal-code" required />
                    <Field label="Country" value={ship.country} onChange={set("country")} testid="ship-country" autoComplete="country" required />
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/35 font-body pt-2">Shipping to US only. International coming soon.</p>
                </div>

                <footer className="border-t border-white/10 px-6 py-5 space-y-3">
                  <div className="space-y-1.5">
                    <div className="flex items-baseline justify-between text-xs font-body">
                      <span className="uppercase tracking-[0.25em] text-white/50">Subtotal</span>
                      <span className="text-white/80">${cart.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex items-baseline justify-between text-xs font-body">
                      <span className="uppercase tracking-[0.25em] text-white/50">Shipping</span>
                      <span className="text-white/80">${(cart.shipping ?? 0).toFixed(2)}</span>
                    </div>
                    <div className="flex items-baseline justify-between pt-2 border-t border-white/10">
                      <span className="text-[11px] uppercase tracking-[0.25em] text-white/60 font-body">Total</span>
                      <span className="font-heading text-3xl text-[#007AFF]">${(cart.total ?? cart.subtotal).toFixed(2)}</span>
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={checkingOut}
                    className="cta-glow w-full py-4 bg-[#007AFF] text-black font-body font-bold text-sm uppercase tracking-[0.25em] hover:bg-white transition-colors disabled:opacity-40 inline-flex items-center justify-center gap-2"
                    data-testid="ship-continue"
                  >
                    {checkingOut ? (<><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</>) : "Continue to Payment"}
                  </button>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/35 text-center font-body">Powered by Stripe · Card details collected securely</p>
                </footer>
              </form>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

const Field = ({ label, testid, ...rest }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.25em] text-white/50 font-body mb-2">{label}</label>
    <input
      data-testid={testid}
      className="w-full bg-[#121212] border border-white/15 px-3 py-3 text-sm font-body focus:border-[#007AFF] focus:outline-none placeholder:text-white/30"
      {...rest}
    />
  </div>
);

export default CartDrawer;
